/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CFormTextarea,
  CButton, CSpinner, CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const WeaveTypeInputs = ({
  onSubmit, onCancel, isLoading = false,
  submitResponse = null, initialData = null, isEdit = false
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    weaveTypeName: '',
    weaveTypeDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        weaveTypeName: initialData.WeaveTypeName || '',
        weaveTypeDescription: initialData.WeaveTypeDescription || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      if (!isEdit) {
        setFormData({ weaveTypeName: '', weaveTypeDescription: '' });
      }
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.weaveTypeName.trim()) newErrors.weaveTypeName = t('weaveTypeNameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const sessionUserId = localStorage.getItem('sessionUserId');
    if (!sessionUserId) {
      setErrors({ general: t('userSessionNotFound') });
      return;
    }

    const payload = {
      SessionUserId: sessionUserId,
      WeaveTypeName: formData.weaveTypeName.trim(),
      WeaveTypeDescription: formData.weaveTypeDescription.trim(),
    };

    if (isEdit && initialData?.WeaveTypeId) {
      payload.WeaveTypeId = initialData.WeaveTypeId;
    }

    onSubmit(payload);
  };

  const handleCancel = () => {
    if (!isEdit) setFormData({ weaveTypeName: '', weaveTypeDescription: '' });
    setErrors({});
    onCancel();
  };

  const getSuccessMessage = () => {
    if (!isEdit) {
      return t('weaveTypeCreated');
    } else {
      return t('weaveTypeUpdated');
    }
  };

  return (
    <div>
      {showSuccess && (
        <CAlert color="success" dismissible onClose={() => setShowSuccess(false)}>
          {getSuccessMessage()}
        </CAlert>
      )}

      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      {submitResponse?.Result && submitResponse.Result.ErrorCode !== 0 && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription || t('somethingWentWrong')}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit}>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="weaveTypeName" style={{ fontStyle: 'normal' }}>
              {t('weaveTypeName')}
            </CFormLabel>
            <CFormInput
              type="text"
              id="weaveTypeName"
              name="weaveTypeName"
              value={formData.weaveTypeName}
              onChange={handleInputChange}
              invalid={!!errors.weaveTypeName}
              disabled={isLoading}
              placeholder={t('enterWeaveTypeName')}
              style={{ fontStyle: 'normal' }}
            />
            {errors.weaveTypeName && (
              <div className="invalid-feedback d-block" style={{ fontStyle: 'normal' }}>
                {errors.weaveTypeName}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="weaveTypeDescription" style={{ fontStyle: 'normal' }}>
              {t('Description')}
            </CFormLabel>
            <CFormTextarea
              id="weaveTypeDescription"
              name="weaveTypeDescription"
              rows={4}
              value={formData.weaveTypeDescription}
              onChange={handleInputChange}
              invalid={!!errors.weaveTypeDescription}
              disabled={isLoading}
              placeholder={t('enterWeaveTypeDescription')}
              style={{ fontStyle: 'normal' }}
            />
            {errors.weaveTypeDescription && (
              <div className="invalid-feedback d-block" style={{ fontStyle: 'normal' }}>
                {errors.weaveTypeDescription}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol className="d-flex gap-2">
            <CButton type="submit" color="primary" disabled={isLoading} style={{ fontStyle: 'normal' }}>
              {isLoading
                ? (<><CSpinner size="sm" className="me-2" /> {t('saving')}</>)
                : (<><CIcon icon={cilSave} className="me-2" /> {t('save')}</>)}
            </CButton>
            <CButton 
              color="secondary" 
              variant="outline" 
              onClick={handleCancel} 
              disabled={isLoading}
              style={{ fontStyle: 'normal' }}
            >
              <CIcon icon={cilX} className="me-2" /> {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

WeaveTypeInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default WeaveTypeInputs;